// Invitation à installer l'application, envoyée par Chrome sur Android au chargement de la page :
// gardée dès le démarrage, car l'écran des paramètres s'ouvre souvent bien après.
import { useEffect, useState } from "react";

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const INSTALLED_KEY = "aubeon-app-installed";

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

const rememberInstalled = () => {
  try {
    localStorage.setItem(INSTALLED_KEY, "1");
  } catch {
    // Stockage refusé (navigation privée) : l'encart reproposera l'installation.
  }
};

export const wasInstalled = () => {
  try {
    return localStorage.getItem(INSTALLED_KEY) === "1";
  } catch {
    return false;
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    // Chrome ne repropose l'installation que si l'application n'est plus là (désinstallée).
    try {
      localStorage.removeItem(INSTALLED_KEY);
    } catch {
      // Stockage refusé : rien à oublier.
    }
    notify();
  });
  // Installée par notre bouton comme par le menu du navigateur.
  window.addEventListener("appinstalled", () => {
    deferred = null;
    rememberInstalled();
    notify();
  });
}

export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const useInstallPrompt = () => {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const listener = () => setVersion((v) => v + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  const install = async () => {
    if (!deferred) return;
    const event = deferred;
    deferred = null;
    await event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === "accepted") rememberInstalled();
    notify();
  };
  return {
    promptAvailable: !!deferred,
    installed: wasInstalled(),
    install,
  };
};
